import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import api from '../utils/api';
import { Btn, Badge, KpiCard, Loading } from '../components/ui';

const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;
// Rate of one operation = SAM × cost/SAM, rounded to paise (as in the rate approval sheet)
const opRate = it => round2(num(it.sam) * num(it.cost_per_sam));

export default function CostingSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState(null);
  const [items, setItems] = useState([]);       // sam / cost_per_sam held as strings while editing
  const [margin, setMargin] = useState('15');   // contractor margin %, string while editing
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = 'Costing sheet';
  }, []);

  const apply = (data) => {
    setSheet(data);
    setItems(data.items.map(i => ({ ...i, sam: String(num(i.sam)), cost_per_sam: String(num(i.cost_per_sam)) })));
    setMargin(String(num(data.contractor_margin_pct)));
    setDirty(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { apply((await api.get(`/costing/sheet/${id}`)).data); }
    catch (e) { setMsg('Error: could not load costing sheet'); }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setField = (idx, f, v) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [f]: v } : it));
    setDirty(true);
  };

  const totalSam = items.reduce((s, i) => s + num(i.sam), 0);
  const sewingRate = round2(items.reduce((s, i) => s + opRate(i), 0));
  const marginPct = num(margin);
  const finalRate = sewingRate * (1 + marginPct / 100);
  const missing = items.filter(i => num(i.cost_per_sam) === 0).length;

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const { data } = await api.put(`/costing/sheet/${id}`, {
        items: items.map(i => ({ id: i.id, sam: i.sam, cost_per_sam: i.cost_per_sam })),
        contractor_margin_pct: margin === '' ? 0 : margin
      });
      apply(data); setMsg('✓ Saved');
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
  };

  const resync = async () => {
    if (!window.confirm('Rebuild this sheet from the OB and costing master? Your SAM / cost-per-SAM edits on this sheet will be lost (the contractor margin is kept).')) return;
    try { apply((await api.post(`/costing/sheet/${id}/resync`)).data); setMsg('✓ Rebuilt from OB'); }
    catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  // Laid out like the Sewing Rate Approval template. Exports what is on screen
  // (including unsaved edits); totals and the final rate are live formulas.
  const downloadExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Sewing Rate');
    ws.columns = [{ width: 40 }, { width: 18 }, { width: 12 }, { width: 12 }, { width: 14 }];
    ws.mergeCells('A1:E1');
    const title = ws.getCell('A1'); title.value = 'Sewing Rate Approval'; title.font = { bold: true, size: 13 };
    const label = (r, k, v) => { ws.getCell(`A${r}`).value = k; ws.getCell(`A${r}`).font = { bold: true }; ws.getCell(`B${r}`).value = v; };
    label(2, 'Style', sheet.style_number);
    label(3, 'Description', sheet.style_description || '');
    label(4, 'OB', sheet.ob_number);
    label(5, 'Buyer / Season', `${sheet.buyer || ''} · ${sheet.season || ''}`);
    const headRow = 11, first = headRow + 1, last = headRow + items.length;
    label(6, 'Total SMV', null);
    ws.getCell('B6').value = { formula: `SUM(C${first}:C${last})`, result: totalSam };
    label(7, 'Sewing Rate', null);
    ws.getCell('B7').value = { formula: `SUM(E${first}:E${last})`, result: sewingRate };
    label(8, 'Contractor Margin Agreed', marginPct / 100);
    ws.getCell('B8').numFmt = '0.##%';
    label(9, 'Final Sewing Rate', null);
    ws.getCell('B9').value = { formula: 'B7*(1+B8)', result: finalRate };
    ws.getCell('B9').font = { bold: true };
    ws.getCell('B6').numFmt = '0.000'; ws.getCell('B7').numFmt = '0.00'; ws.getCell('B9').numFmt = '0.00';
    ws.getCell('B9').alignment = ws.getCell('B8').alignment = ws.getCell('B7').alignment = ws.getCell('B6').alignment = { horizontal: 'left' };

    const head = ws.getRow(headRow);
    head.values = ['Operation', 'Machine', 'SMV', 'Cost / SAM', 'Rate'];
    head.eachCell(c => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F1FB' } }; });
    items.forEach((it, i) => {
      const r = ws.getRow(first + i);
      r.getCell(1).value = it.operation_name;
      r.getCell(2).value = it.machine_type || '';
      r.getCell(3).value = num(it.sam);
      r.getCell(4).value = num(it.cost_per_sam);
      r.getCell(5).value = { formula: `ROUND(C${first + i}*D${first + i},2)`, result: opRate(it) };
    });
    ws.getColumn(3).numFmt = '0.000'; ws.getColumn(4).numFmt = '0.0000'; ws.getColumn(5).numFmt = '0.00';
    ws.getCell('B6').numFmt = '0.000'; ws.getCell('B7').numFmt = '0.00'; ws.getCell('B9').numFmt = '0.00'; ws.getCell('B8').numFmt = '0.##%';

    const buf = await wb.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url; a.download = `sewing_rate_${sheet.ob_number}_${sheet.style_number}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: 18 }}><Loading /></div>;
  if (!sheet) return <div style={{ padding: 18 }}>{msg || 'Costing sheet not found'}</div>;

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Btn size="sm" onClick={() => navigate('/ob')}>← OB</Btn>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>
            {sheet.ob_number}
            <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: 13 }}> · {sheet.style_number}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Buyer: {sheet.buyer} · {sheet.season}</div>
        </div>
        <Btn size="sm" onClick={resync}>Rebuild from OB</Btn>
        <Btn size="sm" onClick={downloadExcel}>Download Excel</Btn>
        <Btn size="sm" variant="primary" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Btn>
      </div>

      {msg && (
        <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 6, marginBottom: 10,
          background: msg.startsWith('✓') ? 'var(--grn-bg)' : 'var(--red-bg)',
          color: msg.startsWith('✓') ? 'var(--grn)' : 'var(--red)' }}>{msg}</div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <KpiCard label="Sewing rate" value={sewingRate.toFixed(2)} />
        <KpiCard label={`Final sewing rate (+${marginPct}%)`} value={finalRate.toFixed(2)} type="blue" />
        <KpiCard label="Total SMV (min)" value={totalSam.toFixed(2)} />
        <KpiCard label="Operations" value={items.length} />
        <KpiCard label="Without cost/SAM" value={missing} type={missing ? 'amber' : 'green'} />
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
        SAM, cost/SAM and the contractor margin can be edited here — changes apply to this costing sheet only, not the OB or the costing master.
        Each operation's rate is SAM × cost/SAM rounded to 2 decimals; the sewing rate is their sum.
      </div>

      <div style={{ background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden' }}>
        <table className="lc-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th><th>Operation</th><th>Machine</th>
              <th style={{ width: 110 }}>SAM (min)</th>
              <th style={{ width: 120 }}>Cost / SAM</th>
              <th style={{ width: 110, textAlign: 'right' }}>Rate</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr key={it.id}>
                <td style={{ color: 'var(--text-secondary)' }}>{idx + 1}</td>
                <td style={{ fontWeight: 500 }}>{it.operation_name}</td>
                <td>{it.machine_type ? <Badge type="blue">{it.machine_type}</Badge> : '—'}</td>
                <td><input className="inp" type="number" min="0" step="0.001" value={it.sam}
                  onChange={e => setField(idx, 'sam', e.target.value)} style={{ width: 90 }} /></td>
                <td><input className="inp" type="number" min="0" step="0.0001" value={it.cost_per_sam}
                  onChange={e => setField(idx, 'cost_per_sam', e.target.value)} style={{ width: 100 }} /></td>
                <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--acc)' }}>{opRate(it).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} style={{ fontWeight: 600, padding: '8px 10px' }}>Sewing rate</td>
              <td style={{ fontWeight: 600 }}>{totalSam.toFixed(3)}</td>
              <td />
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{sewingRate.toFixed(2)}</td>
            </tr>
            <tr>
              <td colSpan={4} style={{ fontWeight: 600, padding: '8px 10px' }}>Contractor margin agreed (%)</td>
              <td>
                <input className="inp" type="number" min="0" max="100" step="0.01" value={margin}
                  onChange={e => { setMargin(e.target.value); setDirty(true); }} style={{ width: 100 }} />
              </td>
              <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{(sewingRate * marginPct / 100).toFixed(2)}</td>
            </tr>
            <tr>
              <td colSpan={5} style={{ fontWeight: 700, padding: '8px 10px' }}>Final sewing rate</td>
              <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--acc)' }}>{finalRate.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}